import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import {
    FaChevronDown, FaChevronUp, FaPlus, FaTrash, FaShieldAlt, FaAward
} from "react-icons/fa";
import styles from "./SifatSiyosati.module.css";
import { testUrl } from "../home/BaseData";

const SifatSiyosati = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(null);
    const [policyData, setPolicyData] = useState([]);
    const [introData, setIntroData] = useState({ content_uz: "" });
    const [loading, setLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);


    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'clean']
        ],
    };

    const initialSections = [
        {
            title: "Umumiy qoidalar",
            content: `<p>1.1. Renessans taʼlim universitetining ichki taʼlim sifatini taʼminlash tizimi taʼlim sifatini oshirish va sifatni yaxshilash madaniyatini rivojlantirish uchun Universitetda tashkil etilgan umumiy yondashuvlar, siyosatlar, asosiy tamoyillar, standartlar va asosiy mexanizmlarni aks ettiradi.</p><p>1.2. Universitet ichki taʼlim sifatini taʼminlash tizimi strategik menejmentning ajralmas qismi boʻlib, boshqa hujjatlar, masalan, Universitetning rivojlanish konsepsiyasi, strategiyasi, akademik siyosati bilan birgalikda koʻrib chiqiladi.</p>`
        }
    ];

    const initialIntro = "Renessans Ta'lim Universiteti ta'lim sifatini doimiy ravishda oshirib borish, xalqaro standartlarga muvofiqlikni ta'minlash va talabalarga yo'naltirilgan ta'lim muhitini yaratishga intiladi.";

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${testUrl}/api/quality-policy/`);

            if (response.data) {
                const { sections, intro } = response.data;
                const lang = i18n.language;

                if (sections && sections.length > 0) {
                    const mappedData = sections.map(item => ({
                        title: item[`title_${lang}`] || item.title_uz,
                        content: item[`content_${lang}`] || item.content_uz
                    }));
                    setPolicyData(mappedData);
                } else {
                    setPolicyData(initialSections);
                }

                if (intro) {
                    setIntroData({
                        content: intro[`content_${lang}`] || intro.content_uz,
                        content_uz: intro.content_uz
                    });
                } else {
                    setIntroData({ content: initialIntro, content_uz: initialIntro });
                }
            }
        } catch (error) {
            console.error("Error fetching quality policy:", error);
            setPolicyData(initialSections);
            setIntroData({ content: initialIntro, content_uz: initialIntro });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [i18n.language]);


    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const handleSave = async () => {
        try {
            const authToken = localStorage.getItem("authToken");
            const payload = {
                sections: policyData,
                intro: {
                    content: introData.content_uz
                }
            };
            const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
            await axios.post(`${testUrl}/api/quality-policy/bulk-sync/`, payload, { headers });
            setIsEditModalOpen(false);
            alert("Ma'lumotlar global miqyosda saqlandi!");
            fetchData();
        } catch (error) {
            console.error("Error saving quality policy:", error);
            alert("Xatolik yuz berdi. Iltimos qaytadan urunib ko'ring.");
        }
    };

    const addNewSection = () => {
        setPolicyData([...policyData, { title: "Yangi bo'lim", content: "" }]);
    };

    const deleteSection = (index) => {
        if (window.confirm("Haqiqatan ham ushbu bo'limni o'chirib tashlamoqchimisiz?")) {
            const newData = policyData.filter((_, i) => i !== index);
            setPolicyData(newData);
        }
    };

    const updateSection = (index, field, value) => {
        const newData = [...policyData];
        newData[index][field] = value;
        setPolicyData(newData);
    };

    const updateIntro = (value) => {
        setIntroData({ ...introData, content: value, content_uz: value });
    };

    if (loading) {
        return <div className={styles.loading}>{t("loading") || "Yuklanmoqda..."}</div>;
    }

    return (
        <div className={styles.mainContainer}>
            <div className={styles.container}>
                <h1 className={styles.mainTitle} data-aos="fade-up">
                    Universitetning ichki taʼlim sifatini <br /> taʼminlash siyosati
                </h1>

                {/* Decorative Divider */}
                <div className={styles.premiumDivider} data-aos="fade-up">
                    <div className={styles.dividerLine}></div>
                    <div className={styles.dividerIcon}>
                        <FaAward />
                    </div>
                    <div className={styles.dividerLine}></div>
                </div>

                <div className={styles.introBox} data-aos="fade-up">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(introData.content || initialIntro) }} />
                </div>

                <div className={styles.accordionContainer} data-aos="fade-up">
                    {policyData.map((item, index) => (
                        <div key={index} className={`${styles.accordionItem} ${activeIndex === index ? styles.active : ""}`}>
                            <button className={styles.accordionHeader} onClick={() => toggleAccordion(index)}>
                                <div className={styles.headerTitle}>
                                    <span className={styles.icon}>
                                        <FaShieldAlt />
                                    </span>
                                    <h2>{item.title}</h2>
                                </div>
                                <div className={styles.headerAction}>
                                    <span className={styles.seeMoreTxt}>{activeIndex === index ? "Yopish" : "Ko'proq ko'rish"}</span>
                                    {activeIndex === index ? <FaChevronUp /> : <FaChevronDown />}
                                </div>
                            </button>
                            <div className={styles.accordionContent}>
                                <div className={styles.innerContent}>
                                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Admin Edit Modal */}
            {isEditModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h2>Siyosatni tahrirlash (Rich Text)</h2>
                            <button className={styles.closeBtn} onClick={() => setIsEditModalOpen(false)}>&times;</button>
                        </div>

                        <div className={styles.formList}>
                            <div className={styles.formItem} style={{ borderBottom: '2px solid #eee', paddingBottom: '20px', marginBottom: '20px' }}>
                                <div className={styles.inputGroup}>
                                    <label style={{ fontWeight: 'bold', color: '#003366' }}>Kirish matni (Rich Text)</label>
                                    <ReactQuill
                                        theme="snow"
                                        value={introData.content_uz}
                                        onChange={updateIntro}
                                        modules={quillModules}
                                    />
                                </div>
                            </div>

                            <label style={{ fontWeight: 'bold', color: '#003366', display: 'block', marginBottom: '10px' }}>Bo'limlar</label>

                            {policyData.map((item, index) => (
                                <div key={index} className={styles.formItem}>
                                    <div className={styles.inputGroup}>
                                        <label>Sarlavha</label>
                                        <input
                                            className={styles.inputField}
                                            value={item.title}
                                            onChange={(e) => updateSection(index, 'title', e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Mazmun (Rich Text)</label>
                                        <ReactQuill
                                            theme="snow"
                                            value={item.content}
                                            onChange={(val) => updateSection(index, 'content', val)}
                                            modules={quillModules}
                                        />
                                    </div>
                                    <div className={styles.itemActions}>
                                        <button className={styles.deleteBtn} onClick={() => deleteSection(index)}>
                                            <FaTrash style={{ marginRight: '5px' }} /> O'chirish
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button className={styles.addBtn} onClick={addNewSection}>
                            <FaPlus /> Yangi bo'lim qo'shish
                        </button>

                        <div className={styles.footerActions}>
                            <button className={styles.saveBtn} onClick={handleSave}>O'zgarishlarni saqlash</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SifatSiyosati;
